import { useQuery } from "@tanstack/react-query";
import { api, endpoints } from "../lib/api";
import Topbar from "../components/Topbar";
import { euro, initials, shortDate } from "../lib/format";
import { Check, Crown } from "lucide-react";

export default function Memberships() {
  const { data: plans = [] } = useQuery({ queryKey: ["plans"], queryFn: () => api.get(endpoints.plans).then((r) => r.data) });
  const { data: summary } = useQuery({ queryKey: ["ms-summary"], queryFn: () => api.get(endpoints.membershipSummary).then((r) => r.data) });
  const { data: clients = [] } = useQuery({ queryKey: ["clients-all"], queryFn: () => api.get(endpoints.clients).then((r) => r.data) });

  return (
    <div>
      <Topbar
        title="Membresías LAKA"
        subtitle="Tres niveles diseñados para acompañar a cada clienta en su ritual de belleza mensual."
        showLogoMark
      />

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8" data-testid="ms-summary">
          <SummaryCard eyebrow="Ingresos recurrentes" value={euro(summary.mrr)} hint="MRR estimado" />
          <SummaryCard eyebrow="Total suscriptoras" value={summary.total_active} hint="Membresías activas" />
          <SummaryCard eyebrow="Distribución"
            value={
              <div className="flex items-baseline gap-3 text-2xl">
                <span title="Silver" style={{ color: "#4A4A4A" }}>{summary.breakdown.silver}</span>
                <span className="text-xs" style={{ color: "var(--laka-text)" }}>·</span>
                <span title="Gold" style={{ color: "#D6B36A" }}>{summary.breakdown.gold}</span>
                <span className="text-xs" style={{ color: "var(--laka-text)" }}>·</span>
                <span title="Black" style={{ color: "#1A1A1A" }}>{summary.breakdown.black}</span>
              </div>
            }
            hint="Silver · Gold · Black"
          />
        </div>
      )}

      {/* Plans */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="ms-plans">
        {plans.map((p) => (
          <PlanCard key={p.id} plan={p} />
        ))}
      </div>

      {/* Subscribers */}
      <div className="laka-card mt-8 p-8" data-testid="ms-subscribers">
        <span className="laka-eyebrow">Suscriptoras activas</span>
        <h3 className="font-display text-2xl mt-1 mb-6">Clientas con membresía</h3>
        <div className="space-y-2">
          {clients.filter((c) => c.membership_plan).map((c) => (
            <div key={c.id} className="flex items-center justify-between py-3 border-b laka-hairline last:border-0">
              <div className="flex items-center gap-3">
                {c.avatar
                  ? <img src={c.avatar} alt="" className="w-9 h-9 rounded-full object-cover" />
                  : <div className="w-9 h-9 rounded-full flex items-center justify-center text-[11px]" style={{ backgroundColor: "#E8DFD1" }}>{initials(c.name)}</div>}
                <div>
                  <div className="font-medium text-sm">{c.name}</div>
                  <div className="text-xs" style={{ color: "var(--laka-text)" }}>Desde {shortDate(c.membership_since)}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: planColor(c.membership_plan) }} />
                LAKA {c.membership_plan.charAt(0).toUpperCase() + c.membership_plan.slice(1)}
              </div>
            </div>
          ))}
          {clients.filter((c) => c.membership_plan).length === 0 && (
            <div className="text-sm py-6" style={{ color: "var(--laka-text)" }}>Aún no hay suscriptoras activas.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ eyebrow, value, hint }) {
  return (
    <div className="laka-card p-6">
      <span className="laka-eyebrow">{eyebrow}</span>
      <div className="font-display text-4xl leading-none mt-3">{value}</div>
      <div className="text-xs mt-2" style={{ color: "var(--laka-text)" }}>{hint}</div>
    </div>
  );
}

function planColor(t) {
  return { silver: "#B8B8B8", gold: "#D6B36A", black: "#1A1A1A" }[t];
}

function PlanCard({ plan }) {
  const isBlack = plan.tier === "black";
  const isGold = plan.tier === "gold";
  const bg = isBlack ? "#1A1A1A" : "#FFFFFF";
  const fg = isBlack ? "#FBF6F2" : "#1A1A1A";
  const border = isBlack ? "transparent" : "#E8DFD1";

  return (
    <div
      data-testid={`plan-card-${plan.tier}`}
      className="rounded-lg p-8 flex flex-col relative overflow-hidden"
      style={{ backgroundColor: bg, color: fg, border: `1px solid ${border}`, boxShadow: "0 4px 20px rgba(26,26,26,0.06)" }}
    >
      {isGold && (
        <div className="absolute top-4 right-4 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ backgroundColor: "#D6B36A", color: "#1A1A1A" }}>
          Más elegido
        </div>
      )}
      <div className="flex items-center gap-2">
        <Crown size={16} strokeWidth={1.25} style={{ color: isBlack ? "#D6B36A" : planColor(plan.tier) }} />
        <span className="laka-eyebrow" style={{ color: isBlack ? "#B8B0A6" : undefined }}>Membresía</span>
      </div>
      <h3 className="font-display text-4xl mt-2 leading-none">{plan.name}</h3>

      <div className="mt-6 flex items-baseline gap-1">
        <span className="font-display text-5xl leading-none" style={{ color: isBlack ? "#D6B36A" : isGold ? "#D6B36A" : "#1A1A1A" }}>
          {plan.price_monthly}€
        </span>
        <span className="text-xs" style={{ color: isBlack ? "#B8B0A6" : "var(--laka-text)" }}>/ mes</span>
      </div>

      <div className="mt-6 laka-divider" style={{ background: isBlack ? "linear-gradient(90deg, transparent, #3a3a3a, transparent)" : undefined }} />

      <ul className="mt-6 space-y-3 flex-1">
        {plan.benefits.map((b) => (
          <li key={b} className="flex gap-2 text-sm" style={{ color: isBlack ? "#E4DED4" : "#1A1A1A" }}>
            <Check size={14} strokeWidth={1.5} style={{ color: isBlack ? "#D6B36A" : "#D6B36A", flexShrink: 0, marginTop: 3 }} />
            <span>{b}</span>
          </li>
        ))}
      </ul>

      <div className="mt-8 flex items-center justify-between pt-6 border-t" style={{ borderColor: isBlack ? "#2a2a2a" : "#E8DFD1" }}>
        <div>
          <div className="text-[10px] uppercase tracking-widest" style={{ color: isBlack ? "#B8B0A6" : "var(--laka-text)" }}>Suscriptoras</div>
          <div className="font-display text-2xl mt-1">{plan.active_clients}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest" style={{ color: isBlack ? "#B8B0A6" : "var(--laka-text)" }}>Ingresos/mes</div>
          <div className="font-display text-2xl mt-1">{euro(plan.active_clients * plan.price_monthly)}</div>
        </div>
      </div>
    </div>
  );
}
